import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import { useLocale } from '../context/LocaleContext';
import {
  BookOpen, Plus, Search, Calendar, Smile, Loader2,
  Trash2, Edit2, Save, X, Download, Filter
} from 'lucide-react';

const MOOD_OPTIONS = [
  { value: 'great', emoji: '😄', label: 'Great' },
  { value: 'good', emoji: '😊', label: 'Good' },
  { value: 'okay', emoji: '😐', label: 'Okay' },
  { value: 'tough', emoji: '😔', label: 'Tough' },
  { value: 'sad', emoji: '😢', label: 'Sad' }
];

const JournalPage = () => {
  const { toast } = useToast();
  const { t } = useLocale();
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [showNewEntry, setShowNewEntry] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterMood, setFilterMood] = useState(null);

  // New entry state
  const [newEntry, setNewEntry] = useState({
    title: '',
    content: '',
    mood: null,
    tags: []
  });

  useEffect(() => {
    loadEntries();
  }, [filterMood]);

  const loadEntries = async () => {
    setLoading(true);
    try {
      let url = `${process.env.REACT_APP_BACKEND_URL}/api/journal/entries?limit=50`;
      if (filterMood) {
        url += `&mood=${filterMood}`;
      }

      const response = await fetch(url, { credentials: 'include' });
      const data = await response.json();

      if (data.success) {
        setEntries(data.entries || []);
      }
    } catch (error) {
      console.error('Failed to load journal entries:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveEntry = async () => {
    if (!newEntry.content.trim()) {
      toast({ title: 'Error', description: 'Please write something', variant: 'destructive' });
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/journal/entries`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(newEntry)
        }
      );

      if (response.ok) {
        toast({ title: '✅ Saved', description: 'Journal entry saved' });
        setNewEntry({ title: '', content: '', mood: null, tags: [] });
        setShowNewEntry(false);
        loadEntries();
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save entry', variant: 'destructive' });
    }
  };

  const deleteEntry = async (entryId) => {
    if (!window.confirm('Delete this journal entry?')) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/journal/entries/${entryId}`,
        { method: 'DELETE', credentials: 'include' }
      );

      if (response.ok) {
        toast({ title: 'Deleted', description: 'Entry removed' });
        setEntries(prev => prev.filter(e => e.id !== entryId));
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete', variant: 'destructive' });
    }
  };

  const exportJournal = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/journal/export`,
        { credentials: 'include' }
      );
      const data = await response.json();

      const blob = new Blob([JSON.stringify(data.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `lifeflo-journal-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);

      toast({ title: 'Exported!', description: 'Journal data downloaded' });
    } catch (error) {
      toast({ title: 'Error', description: 'Export failed', variant: 'destructive' });
    }
  };

  const getMoodEmoji = (mood) => {
    const option = MOOD_OPTIONS.find(m => m.value === mood);
    return option ? option.emoji : '';
  };

  const filteredEntries = entries.filter(entry => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        entry.title?.toLowerCase().includes(query) ||
        entry.content?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 sm:p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <BookOpen className="h-6 w-6 text-purple-500" /> My Journal
            </h1>
            <p className="text-gray-500">Your private space for reflection</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={exportJournal}>
              <Download className="h-4 w-4 mr-1" /> Export
            </Button>
            <Button onClick={() => setShowNewEntry(true)}>
              <Plus className="h-4 w-4 mr-1" /> New Entry
            </Button>
          </div>
        </div>

        {/* Search & Filter */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search entries..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex gap-1">
            <Button
              variant={filterMood === null ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterMood(null)}
            >
              All
            </Button>
            {MOOD_OPTIONS.map(mood => (
              <Button
                key={mood.value}
                variant={filterMood === mood.value ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterMood(filterMood === mood.value ? null : mood.value)}
              >
                {mood.emoji}
              </Button>
            ))}
          </div>
        </div>

        {/* New Entry Form */}
        {showNewEntry && (
          <Card>
            <CardHeader>
              <CardTitle>New Journal Entry</CardTitle>
              <CardDescription>{new Date().toLocaleDateString()}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Input
                placeholder="Title (optional)"
                value={newEntry.title}
                onChange={(e) => setNewEntry({ ...newEntry, title: e.target.value })}
              />
              <Textarea
                placeholder="What's on your mind?"
                value={newEntry.content}
                onChange={(e) => setNewEntry({ ...newEntry, content: e.target.value })}
                rows={6}
              />
              <div>
                <p className="text-sm text-gray-500 mb-2">How are you feeling?</p>
                <div className="flex gap-2">
                  {MOOD_OPTIONS.map(mood => (
                    <button
                      key={mood.value}
                      onClick={() => setNewEntry({ ...newEntry, mood: mood.value })}
                      className={`p-2 rounded-lg text-2xl transition-all ${
                        newEntry.mood === mood.value
                          ? 'bg-purple-100 dark:bg-purple-900 ring-2 ring-purple-500'
                          : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                      }`}
                    >
                      {mood.emoji}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setShowNewEntry(false)}>
                  <X className="h-4 w-4 mr-1" /> Cancel
                </Button>
                <Button onClick={saveEntry}>
                  <Save className="h-4 w-4 mr-1" /> Save Entry
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Entries List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
          </div>
        ) : filteredEntries.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <BookOpen className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No journal entries yet</p>
              <Button className="mt-4" onClick={() => setShowNewEntry(true)}>
                <Plus className="h-4 w-4 mr-1" /> Write Your First Entry
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredEntries.map((entry) => (
              <Card key={entry.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {entry.mood && (
                          <span className="text-xl">{getMoodEmoji(entry.mood)}</span>
                        )}
                        <h3 className="font-semibold">
                          {entry.title || 'Journal Entry'}
                        </h3>
                      </div>
                      <p className="text-gray-600 dark:text-gray-300 whitespace-pre-wrap line-clamp-4">
                        {entry.content}
                      </p>
                      <div className="flex items-center gap-2 mt-3 text-sm text-gray-400">
                        <Calendar className="h-3 w-3" />
                        {new Date(entry.date || entry.created_at).toLocaleDateString()}
                        {entry.tags?.length > 0 && (
                          <div className="flex gap-1">
                            {entry.tags.map(tag => (
                              <Badge key={tag} variant="secondary" className="text-xs">
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteEntry(entry.id)}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default JournalPage;
